/**
 * RemittanceTableHeader Component
 *
 * Responsive header row for the remittances table.
 * Adapts columns based on the layout mode determined by screen width.
 */

import React from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

interface RemittanceTableHeaderProps {
  layout: TableLayoutMode;
  showCheckbox?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onSelectAll?: () => void;
}

export const RemittanceTableHeader: React.FC<RemittanceTableHeaderProps> = ({
  layout,
  showCheckbox,
  allSelected,
  someSelected,
  onSelectAll,
}) => {
  const theme = useTheme();

  if (layout === 'card') return null;

  const headerStyle = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const showTrace = layout === 'full' || layout === 'standard';
  const showMethod = layout === 'full';
  const showDenials = layout === 'full' || layout === 'standard';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: getGridTemplateColumns(layout, showCheckbox),
        gap: GRID_GAP,
        alignItems: 'center',
        px: 2,
        py: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      {showCheckbox && (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={onSelectAll}
          size="small"
          sx={{ p: 0 }}
        />
      )}

      <Typography variant="caption" sx={headerStyle}>
        Payment Date
      </Typography>

      <Typography variant="caption" sx={headerStyle}>
        Payer
      </Typography>

      {showTrace && (
        <Typography variant="caption" sx={headerStyle}>
          Trace #
        </Typography>
      )}

      {showMethod && (
        <Typography variant="caption" sx={headerStyle}>
          Method
        </Typography>
      )}

      <Typography variant="caption" sx={{ ...headerStyle, textAlign: 'right' }}>
        Amount
      </Typography>

      <Typography variant="caption" sx={{ ...headerStyle, textAlign: 'right' }}>
        Claims
      </Typography>

      {showDenials && (
        <Typography variant="caption" sx={{ ...headerStyle, textAlign: 'right' }}>
          Denials
        </Typography>
      )}
    </Box>
  );
};
